import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CreditCard, Shield, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { compressImage, formatFileSize, getCompressionRatio } from '../../services/imageCompressor';
import logger from '../../utils/logger';

// Helper function to convert Google Drive URL to thumbnail URL
const getGoogleDriveThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  // Extract file ID from various Google Drive URL formats
  let fileId = null;

  // Format: https://drive.google.com/file/d/{fileId}/view
  const viewMatch = url.match(/\/file\/d\/([^\/]+)/);
  if (viewMatch) {
    fileId = viewMatch[1];
  }

  // Format: https://drive.google.com/open?id={fileId}
  const openMatch = url.match(/[?&]id=([^&]+)/);
  if (!fileId && openMatch) {
    fileId = openMatch[1];
  }

  if (fileId) {
    // Return thumbnail URL that works for embedded display
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
  }

  return url; // Return original if can't parse
};

const DocumentUpload = ({ onDocumentsChange, existingDocuments = {}, googleDriveImages = {} }) => {
  const [compressing, setCompressing] = useState({});
  // Merge existing documents with Google Drive images
  const initialDocs = { ...existingDocuments };

  // Add Google Drive images as existing documents if not already present
  Object.entries(googleDriveImages).forEach(([key, images]) => {
    if (images && images.length > 0 && !initialDocs[key]) {
      const img = images[0]; // Take first image for single-image fields
      const originalUrl = img.url || img.preview;
      initialDocs[key] = {
        preview: getGoogleDriveThumbnailUrl(originalUrl) || originalUrl,
        name: img.name || 'Previously uploaded',
        isGoogleDrive: true,
        googleDriveUrl: originalUrl
      };
    }
  });

  const [documents, setDocuments] = useState(initialDocs);
  const [dragOver, setDragOver] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    dl: true,
    primary: true,
    secondary: false,
    tertiary: false
  });
  const fileInputRefs = useRef({});

  const documentTypes = {
    dl: {
      title: "Driver's License",
      icon: CreditCard,
      fields: [
        { key: 'dl_front', label: 'Front Side', type: 'dl_front' },
        { key: 'dl_back', label: 'Back Side', type: 'dl_back' }
      ]
    },
    primary: {
      title: 'Primary Insurance',
      icon: Shield,
      fields: [
        { key: 'primary_front', label: 'Front Side', type: 'insurance_front' },
        { key: 'primary_back', label: 'Back Side', type: 'insurance_back' }
      ]
    },
    secondary: {
      title: 'Secondary Insurance',
      icon: Shield,
      fields: [
        { key: 'secondary_front', label: 'Front Side', type: 'insurance_front' },
        { key: 'secondary_back', label: 'Back Side', type: 'insurance_back' }
      ]
    },
    tertiary: {
      title: 'Tertiary Insurance',
      icon: Shield,
      fields: [
        { key: 'tertiary_front', label: 'Front Side', type: 'insurance_front' },
        { key: 'tertiary_back', label: 'Back Side', type: 'insurance_back' }
      ]
    }
  };

  const handleFileSelect = async (key, file) => {
    if (file && file.type.startsWith('image/')) {
      // Show compression indicator
      setCompressing(prev => ({ ...prev, [key]: true }));

      try {
        // Compress image before storing
        const originalSize = file.size;
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8,
          maxSizeKB: 500,
        });

        const compressionRatio = getCompressionRatio(originalSize, compressedFile.size);

        const reader = new FileReader();
        reader.onload = (e) => {
          const newDocs = {
            ...documents,
            [key]: {
              file: compressedFile,
              preview: e.target.result,
              name: compressedFile.name,
              originalSize,
              compressedSize: compressedFile.size,
              compressionRatio,
            }
          };
          setDocuments(newDocs);
          onDocumentsChange && onDocumentsChange(newDocs);
          setCompressing(prev => ({ ...prev, [key]: false }));
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        logger.warn('Compression failed, using original:', error);
        // Fallback to original file
        const reader = new FileReader();
        reader.onload = (e) => {
          const newDocs = {
            ...documents,
            [key]: {
              file,
              preview: e.target.result,
              name: file.name,
              originalSize: file.size,
              compressedSize: file.size,
              compressionRatio: 0,
            }
          };
          setDocuments(newDocs);
          onDocumentsChange && onDocumentsChange(newDocs);
          setCompressing(prev => ({ ...prev, [key]: false }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDrop = (e, key) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    handleFileSelect(key, file);
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    setDragOver(key);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleRemove = (key) => {
    const newDocs = { ...documents };
    delete newDocs[key];
    setDocuments(newDocs);
    onDocumentsChange && onDocumentsChange(newDocs);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Driver's License Front Card SVG
  const DLFrontCard = () => (
    <svg width="140" height="90" viewBox="0 0 140 90" fill="none">
      <rect width="140" height="90" rx="8" fill="#1a5f7a"/>
      <text x="70" y="18" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600">DRIVER'S LICENSE</text>
      {/* Photo placeholder */}
      <rect x="12" y="28" width="35" height="42" rx="4" fill="#fff"/>
      <circle cx="29.5" cy="42" r="10" fill="#b8d4e3"/>
      <path d="M19 58 Q29.5 48 40 58" fill="#4a90a4"/>
      {/* Info lines */}
      <rect x="55" y="32" width="70" height="6" rx="3" fill="#4a90a4"/>
      <rect x="55" y="44" width="60" height="6" rx="3" fill="#4a90a4"/>
      <rect x="55" y="56" width="50" height="6" rx="3" fill="#4a90a4"/>
    </svg>
  );

  // Driver's License Back Card SVG
  const DLBackCard = () => (
    <svg width="140" height="90" viewBox="0 0 140 90" fill="none">
      <rect width="140" height="90" rx="8" fill="#1a5f7a"/>
      {/* Magnetic stripe */}
      <rect x="0" y="15" width="140" height="22" fill="#0d3d4d"/>
      {/* Barcode area */}
      <rect x="35" y="50" width="70" height="28" fill="#0d3d4d"/>
      {/* Barcode lines */}
      {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18].map(i => (
        <rect key={i} x={40 + i * 3.2} y="54" width={i % 3 === 0 ? 2 : 1} height="20" fill="#8ab4c4"/>
      ))}
    </svg>
  );

  // Insurance Front Card SVG
  const InsuranceFrontCard = () => (
    <svg width="140" height="90" viewBox="0 0 140 90" fill="none">
      <rect width="140" height="90" rx="8" fill="#2563eb"/>
      <rect x="10" y="10" width="40" height="15" rx="4" fill="#fff" fillOpacity="0.3"/>
      <text x="30" y="21" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="600">INSURANCE</text>
      <rect x="10" y="35" width="80" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
      <rect x="10" y="45" width="60" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
      <rect x="10" y="55" width="70" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
      <rect x="10" y="70" width="50" height="12" rx="4" fill="#fff" fillOpacity="0.2"/>
    </svg>
  );

  // Insurance Back Card SVG
  const InsuranceBackCard = () => (
    <svg width="140" height="90" viewBox="0 0 140 90" fill="none">
      <rect width="140" height="90" rx="8" fill="#2563eb"/>
      <rect x="10" y="15" width="120" height="20" fill="#1d4ed8"/>
      <rect x="10" y="45" width="100" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
      <rect x="10" y="55" width="80" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
      <rect x="10" y="65" width="90" height="5" rx="2" fill="#fff" fillOpacity="0.5"/>
    </svg>
  );

  const getCardSVG = (type) => {
    switch(type) {
      case 'dl_front': return <DLFrontCard />;
      case 'dl_back': return <DLBackCard />;
      case 'insurance_front': return <InsuranceFrontCard />;
      case 'insurance_back': return <InsuranceBackCard />;
      default: return <DLFrontCard />;
    }
  };

  // Corner brackets component
  const CornerBrackets = () => (
    <>
      {/* Top Left */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '25px',
        height: '25px',
        borderTop: '3px solid #1a5f7a',
        borderLeft: '3px solid #1a5f7a',
        borderTopLeftRadius: '4px'
      }} />
      {/* Top Right */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '25px',
        height: '25px',
        borderTop: '3px solid #1a5f7a',
        borderRight: '3px solid #1a5f7a',
        borderTopRightRadius: '4px'
      }} />
      {/* Bottom Left */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '25px',
        height: '25px',
        borderBottom: '3px solid #1a5f7a',
        borderLeft: '3px solid #1a5f7a',
        borderBottomLeftRadius: '4px'
      }} />
      {/* Bottom Right */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '25px',
        height: '25px',
        borderBottom: '3px solid #1a5f7a',
        borderRight: '3px solid #1a5f7a',
        borderBottomRightRadius: '4px'
      }} />
    </>
  );

  const renderUploadBox = (key, label, type) => {
    const doc = documents[key];
    const isDragOver = dragOver === key;
    const isCompressing = compressing[key];

    return (
      <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <input
          type="file"
          ref={el => fileInputRefs.current[key] = el}
          onChange={(e) => handleFileSelect(key, e.target.files[0])}
          accept="image/*"
          style={{ display: 'none' }}
          disabled={isCompressing}
        />

        {/* Upload Card */}
        <div
          style={{
            width: '100%',
            padding: '32px 24px',
            borderRadius: '12px',
            backgroundColor: isCompressing ? '#f0f9ff' : isDragOver ? '#e8f0f7' : '#f0f4f8',
            border: isCompressing ? '2px solid #3b82f6' : '1px solid #d1dce5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            cursor: doc || isCompressing ? 'default' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: isCompressing ? 0.8 : 1,
          }}
          onDrop={(e) => !isCompressing && handleDrop(e, key)}
          onDragOver={(e) => !isCompressing && handleDragOver(e, key)}
          onDragLeave={handleDragLeave}
          onClick={() => !doc && !isCompressing && fileInputRefs.current[key]?.click()}
        >
          {/* Compressing State */}
          {isCompressing ? (
            <>
              <Loader2 size={40} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '500',
                color: '#3b82f6',
                textAlign: 'center'
              }}>
                Optimizing image...
              </p>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </>
          ) : !doc ? (
            <>
              {/* Icon */}
              <div style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}>
                {getCardSVG(type)}
              </div>

              {/* Title */}
              <p style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '600',
                color: '#334155',
                textAlign: 'center'
              }}>
                {label}
              </p>

              {/* Subtitle */}
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: '#64748b',
                textAlign: 'center'
              }}>
                Drag & drop or click to upload
              </p>

              {/* Choose File Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRefs.current[key]?.click();
                }}
                style={{
                  padding: '8px 24px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  marginTop: '8px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
              >
                Choose File
              </button>
            </>
          ) : (
            <>
              {/* Uploaded Image - handle Google Drive images */}
              {doc.isGoogleDrive ? (
                <a
                  href={doc.googleDriveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', position: 'relative' }}
                >
                  <img
                    src={doc.preview}
                    alt={label}
                    style={{
                      width: '200px',
                      height: '130px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #4285f4'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{
                    width: '200px',
                    height: '130px',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e8f0fe',
                    borderRadius: '8px',
                    border: '2px solid #4285f4',
                    color: '#4285f4',
                    fontSize: '12px',
                    textAlign: 'center',
                    padding: '10px'
                  }}>
                    Click to view on Google Drive
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: '5px',
                    left: '5px',
                    backgroundColor: '#4285f4',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    Google Drive
                  </div>
                </a>
              ) : (
                <img
                  src={doc.preview}
                  alt={label}
                  style={{
                    width: '200px',
                    height: '130px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #d1dce5'
                  }}
                />
              )}

              {/* File Name */}
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '500',
                color: '#334155',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {doc.name}
              </p>

              {/* Google Drive indicator */}
              {doc.isGoogleDrive && (
                <p style={{
                  margin: 0,
                  fontSize: '11px',
                  color: '#4285f4',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontWeight: '600' }}>Previously uploaded</span>
                </p>
              )}

              {/* Compression Info */}
              {doc.compressionRatio > 0 && (
                <p style={{
                  margin: 0,
                  fontSize: '11px',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontWeight: '600' }}>Optimized:</span>
                  {formatFileSize(doc.compressedSize)}
                  <span style={{
                    backgroundColor: '#dcfce7',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    -{doc.compressionRatio}%
                  </span>
                </p>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRefs.current[key]?.click();
                  }}
                  style={{
                    padding: '6px 20px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
                >
                  Change
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(key);
                  }}
                  style={{
                    padding: '6px 20px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                >
                  Remove
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (sectionKey, config) => {
    const isExpanded = expandedSections[sectionKey];
    const IconComponent = config.icon;
    const hasDocuments = config.fields.some(f => documents[f.key]);

    return (
      <div
        key={sectionKey}
        style={{
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}
      >
        {/* Section Header */}
        <div
          onClick={() => toggleSection(sectionKey)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            backgroundColor: hasDocuments ? '#f0fdf4' : '#f9fafb',
            cursor: 'pointer',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconComponent size={24} color={hasDocuments ? '#10b981' : '#6b7280'} />
            <span style={{
              fontWeight: '600',
              fontSize: '16px',
              color: '#1f2937'
            }}>
              {config.title}
            </span>
            {hasDocuments && (
              <span style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                Uploaded
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp size={20} color="#6b7280" />
          ) : (
            <ChevronDown size={20} color="#6b7280" />
          )}
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div style={{
            padding: '30px 20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '30px',
            backgroundColor: '#fafbfc'
          }}>
            {config.fields.map(field => renderUploadBox(field.key, field.label, field.type))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="document-upload-container" style={{ width: '100%' }}>
      <div style={{
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#eff6ff',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <FileText size={24} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{
            margin: '0 0 4px 0',
            fontWeight: '600',
            color: '#1e40af',
            fontSize: '14px'
          }}>
            Document Upload Instructions
          </p>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#3b82f6',
            lineHeight: '1.5'
          }}>
            Please upload clear images of your documents. Accepted formats: JPG, PNG, GIF.
            Make sure all text is readable and the entire document is visible.
          </p>
        </div>
      </div>

      {Object.entries(documentTypes).map(([key, config]) => renderSection(key, config))}

      {/* Summary */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        <p style={{
          margin: '0 0 8px 0',
          fontWeight: '600',
          color: '#374151',
          fontSize: '14px'
        }}>
          Upload Summary
        </p>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {Object.entries(documentTypes).map(([sectionKey, config]) => {
            const uploadedCount = config.fields.filter(f => documents[f.key]).length;
            const totalCount = config.fields.length;
            return (
              <span
                key={sectionKey}
                style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: uploadedCount === totalCount ? '#dcfce7' : uploadedCount > 0 ? '#fef3c7' : '#f3f4f6',
                  color: uploadedCount === totalCount ? '#166534' : uploadedCount > 0 ? '#92400e' : '#6b7280'
                }}
              >
                {config.title}: {uploadedCount}/{totalCount}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
